import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, ShieldAlert } from "lucide-react";
import { api } from "../../lib/api";
import { useCurrency } from "../../lib/currency";
import { toast } from "sonner";

const backend = process.env.REACT_APP_BACKEND_URL;

function download(url, filename) {
  const token = localStorage.getItem("optivize_token");
  fetch(`${backend}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.ok ? r.blob() : Promise.reject(new Error("download failed")))
    .then(blob => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => toast.error("Download failed"));
}

export default function Orders() {
  const { format } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    api.get("/orders/me").then(r => setOrders(r.data)).catch(()=>{});
    api.get("/invoices/me").then(r => setInvoices(r.data)).catch(()=>{});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-3xl font-black tracking-tighter text-white" style={{fontFamily:'Outfit'}}>Orders & Invoices</h1>
        <button
          onClick={() => download(`/api/account/payment-history.pdf`, `payment-history.pdf`)}
          className="btn-ghost inline-flex items-center gap-2"
          data-testid="download-history-btn">
          <FileDown size={14}/> Download payment history
        </button>
      </div>

      <div className="card p-5">
        <h3 className="text-white font-semibold mb-3">Orders</h3>
        {orders.length === 0 ? <p className="text-gray-500 text-sm">No orders.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
                <tr><th className="py-2 px-2 text-left">Order</th><th className="text-left">Items</th><th className="text-left">Date</th><th className="text-right">Total</th><th className="text-center">Status</th><th className="text-right pr-2">PDF</th></tr>
              </thead>
              <tbody>
                {orders.map(o => {
                  const needsKyc = o.status === "awaiting_documents";
                  const kycSent = o.status === "documents_uploaded";
                  return (
                  <tr key={o.id} className="border-b border-white/5" data-testid={`order-row-${o.id}`}>
                    <td className="py-2 px-2 text-cyan-300">#{o.id.slice(0,8)}</td>
                    <td className="text-gray-300 max-w-[280px] truncate">{o.items.map(i=>i.name).join(", ")}</td>
                    <td className="text-gray-400">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="text-right text-white">{format(o.total_inr || o.total)}</td>
                    <td className="text-center">
                      <span className={
                        o.status === "paid" || o.status === "completed" ? "text-emerald-400" :
                        o.status === "approved" ? "text-blue-300" :
                        needsKyc ? "text-amber-400" :
                        kycSent ? "text-cyan-300" :
                        "text-amber-300"
                      }>{o.status.replace("_", " ")}</span>
                      {needsKyc && (
                        <Link
                          to={`/dashboard/orders/${o.id}/kyc`}
                          className="block mt-1 text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
                          data-testid={`upload-kyc-link-${o.id}`}
                        >
                          <ShieldAlert size={11}/> Upload documents →
                        </Link>
                      )}
                      {kycSent && (
                        <div className="text-[11px] text-cyan-400 mt-1">Under review</div>
                      )}
                    </td>
                    <td className="text-right pr-2">
                      <button onClick={()=>download(`/api/account/orders/${o.id}/pdf`, `order-${o.id.slice(0,8).toUpperCase()}.pdf`)} className="text-slate-300 hover:text-white p-1.5" data-testid={`order-pdf-${o.id}`}>
                        <FileDown size={14}/>
                      </button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card p-5">
        <h3 className="text-white font-semibold mb-3">Invoices</h3>
        {invoices.length === 0 ? <p className="text-gray-500 text-sm">No invoices.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
                <tr><th className="py-2 px-2 text-left">Number</th><th className="text-left">Date</th><th className="text-right">Amount</th><th className="text-center">Status</th><th className="text-right pr-2">PDF</th></tr>
              </thead>
              <tbody>
                {invoices.map(i => (
                  <tr key={i.id} className="border-b border-white/5" data-testid={`invoice-row-${i.id}`}>
                    <td className="py-2 px-2 text-cyan-300">{i.number}</td>
                    <td className="text-gray-400">{new Date(i.issued_at).toLocaleDateString()}</td>
                    <td className="text-right text-white">{format(i.amount)}</td>
                    <td className="text-center"><span className="text-emerald-400">{i.status}</span></td>
                    <td className="text-right pr-2">
                      <button onClick={()=>download(`/api/account/invoices/${i.id}/pdf`, `${i.number}.pdf`)} className="text-slate-300 hover:text-white p-1.5" data-testid={`invoice-pdf-${i.id}`}>
                        <FileDown size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
